import { LoadMoreButton, NavigationTab, SearchBar } from "@ai-teach/ui-web";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";

import { AddLessonModal } from "@/components/lessons/AddLessonModal";
import { LessonCard } from "@/components/lessons/LessonCard";
import { DuplicateItemModal } from "@/components/modal/DuplicateItemModal";
import { LinkedParentWarningModal } from "@/components/modal/LinkedParentWarningModal";
import { MoveModal } from "@/components/modal/MoveModal";
import { SelectionActionModal } from "@/components/modal/SelectionActionModal";
import { TrashModal } from "@/components/modal/TrashModal";
import { UnlinkModal } from "@/components/modal/UnlinkModal";
import { WarningModal } from "@/components/modal/WarningModal";
import { ActionButtons } from "@/components/projects/ActionButtons";
import { AddProjectModal } from "@/components/projects/AddProjectModal";
import { ProjectCard } from "@/components/projects/ProjectCard";
import { NoItemFound } from "@/components/shared/NoItemFound";
import { StickyAddButton } from "@/components/shared/StickyAddButton";
import { LessonCardSkeleton, ProjectCardSkeleton } from "@/components/skeletons";
import { SelectionProvider, useSelection } from "@/contexts/SelectionContext";
import {
  GetRootTrashCountDocument,
  type LessonFieldsFragment,
  type ProjectFieldsFragment,
  SortOrder,
} from "@/generated/graphql";
import { ITEM_LIMIT } from "@/lib/constants/shared";
import type { ReorderInfo } from "@/lib/dnd/components/Sortable";
import { Sortable } from "@/lib/dnd/components/Sortable";
import { useLessonsByParent } from "@/lib/hooks/lessons/useLessonsByParent";
import { useReorderLesson } from "@/lib/hooks/lessons/useReorderLesson";
import { useProjectsByParent } from "@/lib/hooks/projects/useProjectsByParent";
import { useReorderProject } from "@/lib/hooks/projects/useReorderProject";
import { useResponsiveSkeletonCount } from "@/lib/hooks/useResponsiveSkeletonCount";
import { useScrollRestoration } from "@/lib/hooks/useScrollRestoration";
import { useDebouncedSearch } from "@/lib/utils/useDebouncedSearch";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { closeEditLessonModal, closeEditProjectModal } from "@/store/uiSlice";
import type { LessonFields } from "@/types/Lesson";
import type { ProjectFields } from "@/types/Project";
import type { ViewMode } from "@/types/viewMode";
import { useQuery } from "@apollo/client/react";
import { MainSidebar } from "../../components/sidebars/MainSidebar";

export type ContentTab = "all" | "projects" | "lesson";

export interface PaginationResponse<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    hasMore: boolean;
  };
}

const tabs = [
  { id: "all", label: "All" },
  { id: "projects", label: "Projects" },
  { id: "lessons", label: "Lessons" },
];

function AdminHomePageContent() {
  const dispatch = useAppDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [searchInput, setSearchInput] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const skeletonCount = useResponsiveSkeletonCount(2);

  const toggleViewMode = () => {
    setViewMode((prev) => (prev === "grid" ? "list" : "grid"));
  };

  const debouncedSearch = useDebouncedSearch(searchInput);

  const {
    projects,
    hasMore: projectsHasMore,
    loading: projectsLoading,
    error: projectsError,
    refetch: refetchProjects,
    loadMore: loadMoreProjects,
  } = useProjectsByParent({
    parentId: null,
    searchText: debouncedSearch,
    skip: activeTab === "lessons",
    isTrash: false,
    sortBy: SortOrder.Order,
  });

  const {
    lessons,
    hasMore: lessonsHasMore,
    loading: lessonsLoading,
    error: lessonsError,
    refetch: refetchLessons,
    loadMore: loadMoreLessons,
  } = useLessonsByParent({
    parentId: null,
    searchText: debouncedSearch,
    skip: activeTab === "projects",
    isTrash: false,
    sortBy: SortOrder.Order,
  });

  const { data: rootTrashCountData } = useQuery(GetRootTrashCountDocument);

  // Infinite scroll triggers (only for individual tabs, not "all")
  const { ref: projectsLoadMoreRef, inView: projectsInView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  const { ref: lessonsLoadMoreRef, inView: lessonsInView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  useScrollRestoration("home", projectsLoading || lessonsLoading);

  const { reorderProject } = useReorderProject();
  const { reorderLesson } = useReorderLesson();

  // Root-level reorder: parentId is null.
  const onProjectReorder = (newItems: ProjectFieldsFragment[], { newIndex }: ReorderInfo) => {
    const movedProject = newItems[newIndex];
    const prev = newItems[newIndex - 1];
    const next = newItems[newIndex + 1];
    const prevSortOrder = prev?.sortOrder ?? null;
    const newSortOrder = next != null ? ((prevSortOrder ?? 0) + (next.sortOrder ?? 0)) / 2 : null;
    if (!movedProject) return;
    return reorderProject(movedProject as ProjectFields, null, prevSortOrder, newSortOrder);
  };

  const onLessonReorder = (newItems: LessonFieldsFragment[], { newIndex }: ReorderInfo) => {
    const movedLesson = newItems[newIndex];
    const prev = newItems[newIndex - 1];
    const next = newItems[newIndex + 1];
    const prevSortOrder = prev?.sortOrder ?? null;
    const newSortOrder = next != null ? ((prevSortOrder ?? 0) + (next.sortOrder ?? 0)) / 2 : null;
    if (!movedLesson) return;
    return reorderLesson(movedLesson as LessonFields, null, prevSortOrder, newSortOrder);
  };

  // Auto-load projects when scrolling (only on projects tab)
  useEffect(() => {
    if (activeTab === "projects" && projectsInView && projectsHasMore && !projectsLoading) {
      loadMoreProjects();
    }
  }, [activeTab, projectsInView, projectsHasMore, projectsLoading, loadMoreProjects]);

  // Auto-load lessons when scrolling (only on lessons tab)
  useEffect(() => {
    if (activeTab === "lessons" && lessonsInView && lessonsHasMore && !lessonsLoading) {
      loadMoreLessons();
    }
  }, [activeTab, lessonsInView, lessonsHasMore, lessonsLoading, loadMoreLessons]);

  const { isEditProjectModalOpen, editingProject, isEditLessonModalOpen, editingLesson } =
    useAppSelector((state) => state.ui);

  const {
    isSelectionMode,
    isTrashModalOpen,
    isDuplicateLessonModalOpen,
    isDuplicateProjectModalOpen,
    isMoveModalOpen,
    isUnlinkModalOpen,
    isWarningModalOpen,
    isLinkedParentWarningModalOpen,
  } = useSelection();

  return (
    <div className="flex h-screen">
      <MainSidebar />

      {/* Main Content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-6">
          {/* Search Bar */}
          <div className="flex items-center gap-3 mb-6">
            <SearchBar value={searchInput} onChange={setSearchInput} />
          </div>

          {/* Tabs + Actions */}
          <div className="flex justify-between items-center mb-8">
            <NavigationTab
              tabs={tabs}
              activeTab={activeTab}
              onChange={(tabId) => setActiveTab(tabId as ContentTab)}
            />
            <ActionButtons
              trashTo="/admin/trash"
              trashParams={{ params: "none" }}
              trashCount={rootTrashCountData?.rootTrashCount ?? 0}
              viewMode={viewMode}
              onToggleView={toggleViewMode}
            />
          </div>

          {/* Projects Grid */}
          {(activeTab === "all" || activeTab === "projects") && (
            <div className="mb-12">
              <h2 className="h3 mb-6">Projects</h2>
              <div
                className={
                  viewMode === "grid"
                    ? `grid ${ITEM_LIMIT.gridCardItemLimitClass} gap-6`
                    : "flex flex-col gap-3 max-w-4xl"
                }
              >
                {projects.length > 0 ? (
                  <Sortable
                    items={projects}
                    onReorder={onProjectReorder}
                    disabled={isSelectionMode || debouncedSearch.trim() !== ""}
                  >
                    {(project) => (
                      <ProjectCard
                        key={project.id}
                        project={project as ProjectFields}
                        viewMode={viewMode}
                        url="home"
                        isSelectionMode={isSelectionMode}
                      />
                    )}
                  </Sortable>
                ) : projectsLoading ? (
                  <>
                    {Array.from({ length: skeletonCount }).map((_, i) => (
                      <ProjectCardSkeleton
                        key={`project-skeleton-card-${i + 1}`}
                        viewMode={viewMode}
                      />
                    ))}
                  </>
                ) : (
                  <NoItemFound itemType="project" error={projectsError} refetch={refetchProjects} />
                )}
              </div>

              {projects.length > 0 && (
                <>
                  {/* Use LoadMoreButton for "all" tab, infinite scroll for "projects" tab */}
                  {activeTab === "all" ? (
                    <LoadMoreButton
                      onClick={loadMoreProjects}
                      hasMore={projectsHasMore}
                      loading={projectsLoading}
                    />
                  ) : (
                    projectsHasMore && (
                      <div ref={projectsLoadMoreRef} className="flex justify-center py-8">
                        {projectsLoading && (
                          <div className="flex items-center gap-2 text-muted">
                            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            <span>Loading more projects...</span>
                          </div>
                        )}
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          )}

          {/* Lessons Grid */}
          {(activeTab === "all" || activeTab === "lessons") && (
            <div className="mb-12">
              <h2 className="h3 mb-6">Lessons</h2>
              <div
                className={
                  viewMode === "grid"
                    ? `grid ${ITEM_LIMIT.gridCardItemLimitClass} gap-5
                      items-start`
                    : "flex flex-col gap-3 max-w-4xl"
                }
              >
                {lessons.length > 0 ? (
                  <Sortable
                    items={lessons}
                    onReorder={onLessonReorder}
                    disabled={isSelectionMode || debouncedSearch.trim() !== ""}
                  >
                    {(lesson) => (
                      <LessonCard
                        key={lesson.id}
                        lesson={lesson as LessonFields}
                        viewMode={viewMode}
                        url="home"
                        isSelectionMode={isSelectionMode}
                        index={null}
                      />
                    )}
                  </Sortable>
                ) : lessonsLoading ? (
                  <>
                    {Array.from({ length: skeletonCount }).map((_, i) => (
                      <LessonCardSkeleton
                        key={`lesson-skeleton-card-${i + 1}`}
                        viewMode={viewMode}
                      />
                    ))}
                  </>
                ) : (
                  <NoItemFound itemType="lesson" error={lessonsError} refetch={refetchLessons} />
                )}
              </div>

              {lessons.length > 0 && (
                <>
                  {/* Use LoadMoreButton for "all" tab, infinite scroll for "lessons" tab */}
                  {activeTab === "all" ? (
                    <LoadMoreButton
                      onClick={loadMoreLessons}
                      hasMore={lessonsHasMore}
                      loading={lessonsLoading}
                    />
                  ) : (
                    lessonsHasMore && (
                      <div ref={lessonsLoadMoreRef} className="flex justify-center py-8">
                        {lessonsLoading && (
                          <div className="flex items-center gap-2 text-muted">
                            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            <span>Loading more lessons...</span>
                          </div>
                        )}
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <StickyAddButton scrollContainerRef={scrollContainerRef} />

      {/* Modals */}
      {editingProject && (
        <AddProjectModal
          isOpen={isEditProjectModalOpen}
          onClose={() => dispatch(closeEditProjectModal())}
          parentId={null}
          project={editingProject}
        />
      )}
      {editingLesson && (
        <AddLessonModal
          isOpen={isEditLessonModalOpen}
          onClose={() => dispatch(closeEditLessonModal())}
          parentId={null}
          lesson={editingLesson}
        />
      )}
      {isSelectionMode && <SelectionActionModal page="projectPage" isOpen={isSelectionMode} />}
      {isMoveModalOpen && <MoveModal isOpen={isMoveModalOpen} />}
      {isTrashModalOpen && <TrashModal isOpen={isTrashModalOpen} sourceId={null} />}
      {isDuplicateLessonModalOpen && <DuplicateItemModal isOpen={true} itemType="lesson" />}
      {isDuplicateProjectModalOpen && <DuplicateItemModal isOpen={true} itemType="project" />}
      {isUnlinkModalOpen && <UnlinkModal isOpen={true} />}
      {isWarningModalOpen && <WarningModal isOpen={true} />}
      {isLinkedParentWarningModalOpen && (
        <LinkedParentWarningModal isOpen={isLinkedParentWarningModalOpen} />
      )}
    </div>
  );
}

export function AdminHomePage() {
  return (
    <SelectionProvider>
      <AdminHomePageContent />
    </SelectionProvider>
  );
}
